import {
  Connection,
  ConnectionConfiguration,
  ConnectionConfigurationCreate,
  ConnectionCreate,
  MongoDBConnectionConfigurationBE,
} from "src/api/connectApi/types";
import {
  filterSecretsByEnvironment,
  filterUpdatedSecrets,
  findSecret,
  getDefaultSecret,
  markSecrets,
  setSecretEnv,
} from "src/connections/model/common";
import {
  KeyValuePair,
  MongoDBConnectionConfig,
  MongoDBConnectionConfigInputs,
} from "src/connections/types";

export const getMongoDBConnectionConfigDefaultValues = (
  env: string | null,
): MongoDBConnectionConfig => ({
  databaseName: "",
  host: "",
  nonSrvHostString: "",
  authSource: "",
  allowTestInvocations: false,
  authMethod: "username_password",
  usernamePasswordConfig: {
    username: getDefaultSecret("username", env),
    password: getDefaultSecret("password", env),
  },
});

export const getMongoDBConnectionConfigInputsDefaultValues =
  (): MongoDBConnectionConfigInputs => ({
    name: "",
    enableNonProdConfigs: false,
    dataRetention: {
      value: 0,
      unit: "days",
    },
    productionConfig: getMongoDBConnectionConfigDefaultValues(null),
    sandboxConfig: getMongoDBConnectionConfigDefaultValues("sandbox"),
  });

const convertBEConnectionConfigurationToMongoDBConnectionConfig = (
  config: ConnectionConfiguration,
  envSecrets: KeyValuePair[],
  env: string | null,
): MongoDBConnectionConfig => {
  const mongoDBConfig = config as MongoDBConnectionConfigurationBE;
  return {
    databaseName: mongoDBConfig.database_name,
    host: mongoDBConfig.host,
    allowTestInvocations: config.allow_test_invocations ?? false,
    authMethod: mongoDBConfig.auth_method,
    nonSrvHostString: mongoDBConfig.non_srv_host_string,
    authSource: mongoDBConfig.auth_source,
    usernamePasswordConfig: {
      username:
        findSecret(envSecrets, "username") ?? getDefaultSecret("username", env),
      password:
        findSecret(envSecrets, "password") ?? getDefaultSecret("password", env),
    },
  };
};

const convertBENonProdEnvConfigToMongoDBConnectionConfigs = (
  nonProdEnvConfigs: Record<string, ConnectionConfiguration>,
  env: string,
  allSecrets: KeyValuePair[],
): MongoDBConnectionConfig | null => {
  if (!nonProdEnvConfigs?.[env]) return null;
  const envSecrets = filterSecretsByEnvironment(allSecrets, env);
  return convertBEConnectionConfigurationToMongoDBConnectionConfig(
    nonProdEnvConfigs[env],
    envSecrets,
    env,
  );
};

/**
 * Converts BE MongoDB connection configuration to FE format.
 * If the provider of the connection is not "mongodb", it throws an error.
 *
 * @param {Connection} connection - The MongoDB connection object in BE format.
 * @returns {MongoDBConnectionConfigInputs} - The converted MongoDB connection object in FE format suitable to use with components.
 * @throws {Error} - Throws an error if the provider of the connection is not "mongodb".
 */
export const convertBEConnectionToMongoDBConnectionConfigInputs = (
  connection: Connection,
): MongoDBConnectionConfigInputs => {
  if (connection.provider !== "mongodb")
    throw new Error(
      `Invalid connection type: ${connection.provider} for MongoDB converter`,
    );

  const defaultValues = getMongoDBConnectionConfigInputsDefaultValues();
  const nonProdEnvConfigs = connection.non_prod_env_configs;

  const allSecrets = markSecrets(connection.secrets) ?? [];
  const prodSecrets = filterSecretsByEnvironment(allSecrets, null);
  return {
    ...defaultValues,
    name: connection.name,
    enableNonProdConfigs: connection.enable_non_prod_configs,
    dataRetention: connection.data_retention ?? defaultValues.dataRetention,
    productionConfig: convertBEConnectionConfigurationToMongoDBConnectionConfig(
      connection.configuration,
      prodSecrets,
      null,
    ),
    sandboxConfig:
      nonProdEnvConfigs && nonProdEnvConfigs["sandbox"]
        ? convertBENonProdEnvConfigToMongoDBConnectionConfigs(
            nonProdEnvConfigs,
            "sandbox",
            allSecrets,
          )
        : getMongoDBConnectionConfigDefaultValues("sandbox"),
  };
};

const extractSecretsFromMongoDBConnectionConfig = (
  config: MongoDBConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.authMethod === "username_password") {
    return [
      setSecretEnv(config.usernamePasswordConfig.username, env),
      setSecretEnv(config.usernamePasswordConfig.password, env),
    ];
  }
  return [];
};

const extractSecretsFromMongoDBConnectionConfigInputs = (
  config: MongoDBConnectionConfigInputs,
): KeyValuePair[] => {
  const secrets = extractSecretsFromMongoDBConnectionConfig(
    config.productionConfig,
    null,
  );
  // Only include non-prod secrets if non-prod configs are enabled
  if (config.enableNonProdConfigs && config.sandboxConfig) {
    secrets.push(
      ...extractSecretsFromMongoDBConnectionConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
  }
  // Removing un-changed secrets, so that they are not sent to the backend
  return filterUpdatedSecrets(secrets);
};

const convertMongoDBConnectionConfigToBEConnectionConfiguration = (
  config: MongoDBConnectionConfig,
): ConnectionConfigurationCreate => {
  return {
    database_name: config.databaseName,
    host: config.host,
    non_srv_host_string: config.nonSrvHostString,
    auth_source: config.authSource,
    auth_method: config.authMethod,
    allow_test_invocations: config.allowTestInvocations,
  };
};

const stripRequiredFromSecrets = (secrets: KeyValuePair[]): KeyValuePair[] =>
  secrets.map((secret) => {
    // Clone the secret so we do not mutate the original object
    const sanitized: KeyValuePair = { ...secret } as KeyValuePair;
    // Remove the `required` field unless the secret is required and value is empty string
    if (!(secret.required && secret.value === "")) {
      delete sanitized.required;
    }
    return sanitized;
  });

/**
 * Converts FE MongoDB connection object to BE format.
 *
 * @param {MongoDBConnectionConfigInputs} inputs - The MongoDB connection object in FE format.
 * @returns {ConnectionCreate} - The MongoDB connection object in BE format suitable for sending POST request.
 */
export const convertMongoDBConnectionConfigInputsToBEConnection = (
  inputs: MongoDBConnectionConfigInputs,
): ConnectionCreate => {
  const secrets = stripRequiredFromSecrets(
    extractSecretsFromMongoDBConnectionConfigInputs(inputs),
  );

  return {
    name: inputs.name,
    is_sandbox: false,
    provider: "mongodb",
    configuration: convertMongoDBConnectionConfigToBEConnectionConfiguration(
      inputs.productionConfig,
    ),
    secrets,
    data_retention: inputs.dataRetention,
    enable_non_prod_configs: inputs.enableNonProdConfigs,
    non_prod_env_configs:
      inputs.enableNonProdConfigs && inputs.sandboxConfig
        ? {
            sandbox: convertMongoDBConnectionConfigToBEConnectionConfiguration(
              inputs.sandboxConfig,
            ),
          }
        : {},
  };
};
